/**
 * PromotionTierTable — Inline-editable tier row table
 *
 * Auto-gap-fill: when max_qty of row N changes, set min_qty of row N+1 = max_qty + 1.
 * When min_qty of row N changes, set max_qty of row N-1 = min_qty - 1.
 * Max 10 tiers. Last tier max_qty is optional (unlimited).
 *
 * Part of Plan 199, Epic 02.
 */

import { useCallback } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export interface TierRow {
  localId: string;
  min_qty: string;
  max_qty: string;
  discount_percentage: string;
  perk_names: string[];
}

interface Props {
  tiers: TierRow[];
  availablePerks: string[];
  onChange: (tiers: TierRow[]) => void;
}

const MAX_TIERS = 10;

export function PromotionTierTable({ tiers, availablePerks, onChange }: Props) {
  const updateRow = useCallback(
    (index: number, field: keyof TierRow, value: string | string[]) => {
      const next = [...tiers];
      next[index] = { ...next[index], [field]: value };

      // Auto-gap-fill: toQty → next row's fromQty
      if (field === "max_qty" && index < next.length - 1 && value !== "") {
        const toVal = parseInt(value as string);
        if (!isNaN(toVal)) {
          next[index + 1] = {
            ...next[index + 1],
            min_qty: String(toVal + 1),
          };
        }
      }

      // Auto-gap-fill: fromQty → prev row's toQty
      if (field === "min_qty" && index > 0 && value !== "") {
        const fromVal = parseInt(value as string);
        if (!isNaN(fromVal) && fromVal > 1) {
          next[index - 1] = {
            ...next[index - 1],
            max_qty: String(fromVal - 1),
          };
        }
      }

      onChange(next);
    },
    [tiers, onChange]
  );

  const addRow = useCallback(() => {
    if (tiers.length >= MAX_TIERS) return;
    const lastTier = tiers[tiers.length - 1];
    const nextMin = lastTier?.max_qty
      ? String(parseInt(lastTier.max_qty) + 1)
      : lastTier
        ? String(parseInt(lastTier.min_qty || "1") + 1)
        : "1";

    onChange([
      ...tiers,
      {
        localId: crypto.randomUUID(),
        min_qty: nextMin,
        max_qty: "",
        discount_percentage: "",
        perk_names: [],
      },
    ]);
  }, [tiers, onChange]);

  const removeRow = useCallback(
    (index: number) => {
      if (tiers.length <= 1) return;
      const next = tiers.filter((_, i) => i !== index);
      onChange(next);
    },
    [tiers, onChange]
  );

  const togglePerk = useCallback(
    (index: number, perkName: string) => {
      const row = tiers[index];
      const current = row.perk_names || [];
      const next = current.includes(perkName)
        ? current.filter((p) => p !== perkName)
        : [...current, perkName];
      updateRow(index, "perk_names", next);
    },
    [tiers, updateRow]
  );

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="grid grid-cols-[80px_80px_100px_1fr_36px] gap-2 px-1">
        <span className="text-xs font-medium text-muted-foreground">
          Min Qty
        </span>
        <span className="text-xs font-medium text-muted-foreground">
          Max Qty
        </span>
        <span className="text-xs font-medium text-muted-foreground">
          Discount %
        </span>
        <span className="text-xs font-medium text-muted-foreground">
          Perks
        </span>
        <span />
      </div>

      {/* Rows */}
      {tiers.map((tier, index) => {
        const isLast = index === tiers.length - 1;
        const minQtyErr = validateMinQty(tier, index, tiers);
        const maxQtyErr = validateMaxQty(tier, isLast);
        const discountErr = validateDiscount(tier);

        return (
          <div
            key={tier.localId}
            className="grid grid-cols-[80px_80px_100px_1fr_36px] gap-2 items-start"
          >
            <div>
              <Input
                type="number"
                min={1}
                value={tier.min_qty}
                onChange={(e) => updateRow(index, "min_qty", e.target.value)}
                className={`h-9 text-sm ${minQtyErr ? "border-red-400" : ""}`}
                placeholder="1"
              />
              {minQtyErr && (
                <p className="text-[10px] text-red-500 mt-0.5">{minQtyErr}</p>
              )}
            </div>
            <div>
              <Input
                type="number"
                min={1}
                value={tier.max_qty}
                onChange={(e) => updateRow(index, "max_qty", e.target.value)}
                className={`h-9 text-sm ${maxQtyErr ? "border-red-400" : ""}`}
                placeholder={isLast ? "∞" : ""}
              />
              {maxQtyErr && (
                <p className="text-[10px] text-red-500 mt-0.5">{maxQtyErr}</p>
              )}
            </div>
            <div>
              <Input
                type="number"
                min={0}
                max={100}
                value={tier.discount_percentage}
                onChange={(e) =>
                  updateRow(index, "discount_percentage", e.target.value)
                }
                className={`h-9 text-sm ${discountErr ? "border-red-400" : ""}`}
                placeholder="0"
              />
              {discountErr && (
                <p className="text-[10px] text-red-500 mt-0.5">
                  {discountErr}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-1 items-center min-h-[36px]">
              {availablePerks.map((perk) => {
                const isActive = (tier.perk_names || []).includes(perk);
                return (
                  <Badge
                    key={perk}
                    variant={isActive ? "default" : "outline"}
                    className={`cursor-pointer text-[10px] px-1.5 py-0 h-5 ${
                      isActive
                        ? "bg-green-100 text-green-700 hover:bg-green-200 border-green-200"
                        : "hover:bg-muted"
                    }`}
                    onClick={() => togglePerk(index, perk)}
                  >
                    {perk}
                  </Badge>
                );
              })}
              {availablePerks.length === 0 && (
                <span className="text-[10px] text-muted-foreground">
                  Define perks below first
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-red-500"
              onClick={() => removeRow(index)}
              disabled={tiers.length <= 1}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        );
      })}

      {/* Add Row */}
      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-muted-foreground"
        onClick={addRow}
        disabled={tiers.length >= MAX_TIERS}
      >
        <Plus className="w-3.5 h-3.5 mr-1" />
        Add Tier {tiers.length >= MAX_TIERS && "(max 10)"}
      </Button>
    </div>
  );
}

// ── Validation ───────────────────────────────────────────────

function validateMinQty(
  tier: TierRow,
  index: number,
  allTiers: TierRow[]
): string | null {
  if (!tier.min_qty) return "Required";
  const val = parseInt(tier.min_qty);
  if (isNaN(val) || val < 1) return "Min 1";
  if (index > 0) {
    const prevMax = parseInt(allTiers[index - 1].max_qty);
    if (!isNaN(prevMax) && val !== prevMax + 1) return `Should be ${prevMax + 1}`;
  }
  return null;
}

function validateMaxQty(tier: TierRow, isLast: boolean): string | null {
  if (isLast && !tier.max_qty) return null; // last tier unlimited is OK
  if (!isLast && !tier.max_qty) return "Required";
  if (tier.max_qty) {
    const val = parseInt(tier.max_qty);
    const minVal = parseInt(tier.min_qty);
    if (isNaN(val)) return "Invalid";
    if (!isNaN(minVal) && val < minVal) return `Must be ≥ ${minVal}`;
  }
  return null;
}

function validateDiscount(tier: TierRow): string | null {
  if (!tier.discount_percentage) return "Required";
  const val = parseFloat(tier.discount_percentage);
  if (isNaN(val)) return "Invalid";
  if (val < 0 || val > 100) return "0-100";
  return null;
}

/** Check if all tiers are valid. Used by parent form to gate save. */
export function areTiersValid(tiers: TierRow[]): boolean {
  if (tiers.length === 0) return false;
  return tiers.every((tier, index) => {
    const isLast = index === tiers.length - 1;
    return (
      !validateMinQty(tier, index, tiers) &&
      !validateMaxQty(tier, isLast) &&
      !validateDiscount(tier)
    );
  });
}
